import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Briefcase, Scale, Plane } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './LuggageWeightCalculatorPage/components/CalculationForm';
import ResultsOverview from './LuggageWeightCalculatorPage/components/ResultsOverview';
import AirlineComparisonTable from './LuggageWeightCalculatorPage/components/AirlineComparisonTable';
import QuickAdjustmentSidebar from './LuggageWeightCalculatorPage/components/QuickAdjustmentSidebar';
import TipsSidebar from './LuggageWeightCalculatorPage/components/TipsSidebar';
import AirlineInfoSidebar from './LuggageWeightCalculatorPage/components/AirlineInfoSidebar';
import InfoContent from './LuggageWeightCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  airline: 'aeroflot',
  travelClass: 'economy',
  destinationType: 'domestic',
  passengerType: 'adult',
  baggageWeight: '20',
  carryOnWeight: '5',
  carryOnCount: '1',
  baggageCount: '1',
  hasLoyaltyStatus: false,
  loyaltyStatus: 'standard',
  hasExtraItems: false,
  extraItems: [],
  includeOverweight: false,
  overweightKg: '5'
};

// Определение типов путешествий
export const destinationTypes = [
  { id: 'domestic', name: 'Внутренние рейсы', description: 'Рейсы внутри России' },
  { id: 'cis', name: 'Страны СНГ', description: 'Рейсы в страны СНГ и ближнего зарубежья' },
  { id: 'international', name: 'Международные рейсы', description: 'Международные рейсы за пределы СНГ' }
];

// Определение классов обслуживания
export const travelClasses = [
  { id: 'economy', name: 'Эконом-класс', description: 'Базовый класс обслуживания' },
  { id: 'comfort', name: 'Комфорт', description: 'Улучшенный эконом-класс с расширенным пространством для ног' },
  { id: 'business', name: 'Бизнес-класс', description: 'Премиальный класс с повышенным комфортом' },
  { id: 'first', name: 'Первый класс', description: 'Наивысший класс обслуживания (доступен на ограниченных рейсах)' }
];

// Определение типов пассажиров
export const passengerTypes = [
  { id: 'adult', name: 'Взрослый', description: 'Пассажиры от 12 лет и старше' },
  { id: 'child', name: 'Ребенок (2-11 лет)', description: 'Дети от 2 до 11 лет' },
  { id: 'infant', name: 'Младенец (до 2 лет)', description: 'Дети до 2 лет' }
];

// Определение списка дополнительных предметов
export const extraItemsList = [
  { id: 'sport_equipment', name: 'Спортивный инвентарь', weight: 15, description: 'Лыжи, сноуборд, гольф и т.д.' },
  { id: 'musical_instrument', name: 'Музыкальный инструмент', weight: 8, description: 'Гитара, скрипка и другие' },
  { id: 'pet', name: 'Животное в переноске', weight: 8, description: 'Кошка, собака, птица' },
  { id: 'stroller', name: 'Детская коляска', weight: 10, description: 'Прогулочная коляска для ребенка' },
  { id: 'wheelchair', name: 'Инвалидное кресло', weight: 15, description: 'Складное кресло-коляска' },
  { id: 'medical_equipment', name: 'Медицинское оборудование', weight: 10, description: 'Необходимые медицинские приборы' }
];

// Данные авиакомпаний
export const airlinesData = [
  {
    id: 'aeroflot',
    name: 'Аэрофлот',
    logo: '/images/airlines/aeroflot.png',
    // Нормы багажа для различных классов и направлений
    baggageAllowance: {
      domestic: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 3, carryOnWeight: 15, carryOnCount: 2, }
      },
      cis: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 3, carryOnWeight: 15, carryOnCount: 2, }
      },
      international: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 3, carryOnWeight: 15, carryOnCount: 2, }
      }
    },
    // Тарифы для дополнительного багажа
    extraBaggageFees: {
      domestic: {
        overweight: { base: 2500, perKg: 500 }, // Базовая стоимость + доплата за кг
        extraPiece: 2500 // Стоимость дополнительного места багажа
      },
      cis: {
        overweight: { base: 3000, perKg: 700 },
        extraPiece: 3000
      },
      international: {
        overweight: { base: 4000, perKg: 800 },
        extraPiece: 4000
      }
    },
    // Бонусы для участников программы лояльности
    loyaltyProgram: {
      standard: { extraWeight: 0, extraCount: 0 },
      silver: { extraWeight: 5, extraCount: 0 },
      gold: { extraWeight: 10, extraCount: 1 },
      platinum: { extraWeight: 15, extraCount: 1 }
    },
    // Особые условия
    specialConditions: {
      infant: { carryOnAllowance: true, baggageAllowance: true },
      child: { carryOnAllowance: true, baggageAllowance: true },
      medical: { freeWeight: 5 }
    }
  },
  {
    id: 's7',
    name: 'S7 Airlines',
    logo: '/images/airlines/s7.png',
    baggageAllowance: {
      domestic: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, }
      },
      cis: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, }
      },
      international: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, }
      }
    },
    extraBaggageFees: {
      domestic: {
        overweight: { base: 2000, perKg: 400 },
        extraPiece: 2000
      },
      cis: {
        overweight: { base: 2500, perKg: 600 },
        extraPiece: 2500
      },
      international: {
        overweight: { base: 3500, perKg: 700 },
        extraPiece: 3500
      }
    },
    loyaltyProgram: {
      standard: { extraWeight: 0, extraCount: 0 },
      silver: { extraWeight: 5, extraCount: 0 },
      gold: { extraWeight: 10, extraCount: 1 },
      platinum: { extraWeight: 15, extraCount: 1 }
    },
    specialConditions: {
      infant: { carryOnAllowance: true, baggageAllowance: true },
      child: { carryOnAllowance: true, baggageAllowance: true },
      medical: { freeWeight: 5 }
    }
  },
  {
    id: 'pobeda',
    name: 'Победа',
    logo: '/images/airlines/pobeda.png',
    baggageAllowance: {
      domestic: {
        economy: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        first: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, }
      },
      cis: {
        economy: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        first: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, }
      },
      international: {
        economy: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        first: { weight: 20, count: 1, carryOnWeight: 10, carryOnCount: 1, }
      }
    },
    extraBaggageFees: {
      domestic: {
        overweight: { base: 2000, perKg: 500 },
        extraPiece: 2000
      },
      cis: {
        overweight: { base: 2000, perKg: 500 },
        extraPiece: 2000
      },
      international: {
        overweight: { base: 2500, perKg: 700 },
        extraPiece: 2500
      }
    },
    loyaltyProgram: {
      standard: { extraWeight: 0, extraCount: 0 },
      silver: { extraWeight: 0, extraCount: 0 },
      gold: { extraWeight: 5, extraCount: 0 },
      platinum: { extraWeight: 10, extraCount: 0 }
    },
    specialConditions: {
      infant: { carryOnAllowance: true, baggageAllowance: false },
      child: { carryOnAllowance: true, baggageAllowance: true },
      medical: { freeWeight: 5 }
    }
  },
  {
    id: 'utair',
    name: 'ЮТэйр',
    logo: '/images/airlines/utair.png',
    baggageAllowance: {
      domestic: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, }
      },
      cis: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, }
      },
      international: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, }
      }
    },
    extraBaggageFees: {
      domestic: {
        overweight: { base: 2000, perKg: 500 },
        extraPiece: 2000
      },
      cis: {
        overweight: { base: 2500, perKg: 600 },
        extraPiece: 2500
      },
      international: {
        overweight: { base: 3000, perKg: 700 },
        extraPiece: 3000
      }
    },
    loyaltyProgram: {
      standard: { extraWeight: 0, extraCount: 0 },
      silver: { extraWeight: 5, extraCount: 0 },
      gold: { extraWeight: 10, extraCount: 0 },
      platinum: { extraWeight: 15, extraCount: 1 }
    },
    specialConditions: {
      infant: { carryOnAllowance: true, baggageAllowance: false },
      child: { carryOnAllowance: true, baggageAllowance: true },
      medical: { freeWeight: 5 }
    }
  },
  {
    id: 'redwings',
    name: 'Red Wings',
    logo: '/images/airlines/redwings.png',
    baggageAllowance: {
      domestic: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 1, carryOnWeight: 15, carryOnCount: 1, },
        first: { weight: 32, count: 1, carryOnWeight: 15, carryOnCount: 1, }
      },
      cis: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 1, carryOnWeight: 15, carryOnCount: 1, },
        first: { weight: 32, count: 1, carryOnWeight: 15, carryOnCount: 1, }
      },
      international: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 1, carryOnWeight: 15, carryOnCount: 1, },
        first: { weight: 32, count: 1, carryOnWeight: 15, carryOnCount: 1, }
      }
    },
    extraBaggageFees: {
      domestic: {
        overweight: { base: 1500, perKg: 300 },
        extraPiece: 1500
      },
      cis: {
        overweight: { base: 2000, perKg: 500 },
        extraPiece: 2000
      },
      international: {
        overweight: { base: 2500, perKg: 600 },
        extraPiece: 2500
      }
    },
    loyaltyProgram: {
      standard: { extraWeight: 0, extraCount: 0 },
      silver: { extraWeight: 5, extraCount: 0 },
      gold: { extraWeight: 10, extraCount: 0 },
      platinum: { extraWeight: 10, extraCount: 1 }
    },
    specialConditions: {
      infant: { carryOnAllowance: true, baggageAllowance: false },
      child: { carryOnAllowance: true, baggageAllowance: true },
      medical: { freeWeight: 5 }
    }
  },
  {
    id: 'rossiya',
    name: 'Россия',
    logo: '/images/airlines/rossiya.png',
    baggageAllowance: {
      domestic: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, }
      },
      cis: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, }
      },
      international: {
        economy: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        comfort: { weight: 23, count: 1, carryOnWeight: 10, carryOnCount: 1, },
        business: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, },
        first: { weight: 32, count: 2, carryOnWeight: 15, carryOnCount: 2, }
      }
    },
    extraBaggageFees: {
      domestic: {
        overweight: { base: 2500, perKg: 500 },
        extraPiece: 2500
      },
      cis: {
        overweight: { base: 3000, perKg: 700 },
        extraPiece: 3000
      },
      international: {
        overweight: { base: 4000, perKg: 800 },
        extraPiece: 4000
      }
    },
    loyaltyProgram: {
      standard: { extraWeight: 0, extraCount: 0 },
      silver: { extraWeight: 5, extraCount: 0 },
      gold: { extraWeight: 10, extraCount: 1 },
      platinum: { extraWeight: 15, extraCount: 1 }
    },
    specialConditions: {
      infant: { carryOnAllowance: true, baggageAllowance: true },
      child: { carryOnAllowance: true, baggageAllowance: true },
      medical: { freeWeight: 5 }
    }
  }
];

// Получение списка статусов лояльности
export const loyaltyStatuses = [
  { id: 'standard', name: 'Базовый', description: 'Стандартный уровень участника программы лояльности' },
  { id: 'silver', name: 'Серебряный', description: 'Повышенные привилегии для постоянных клиентов' },
  { id: 'gold', name: 'Золотой', description: 'Высокий уровень привилегий для часто летающих пассажиров' },
  { id: 'platinum', name: 'Платиновый', description: 'Максимальный уровень привилегий' }
];

/**
 * LuggageWeightCalculatorPage Component - Калькулятор багажа для разных авиакомпаний
 * с учетом классов обслуживания, направлений и статусов лояльности.
 */
const LuggageWeightCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('luggage-weight-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор массы багажа | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте допустимую массу багажа и ручной клади для российских и международных авиакомпаний. Узнайте стоимость перевеса и дополнительного багажа с учетом класса обслуживания и программы лояльности.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showAllAirlines, setShowAllAirlines] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedLuggageCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'aeroflot':
          newState.airline = 'aeroflot';
          break;
        case 's7':
          newState.airline = 's7';
          break;
        case 'international':
          newState.destinationType = 'international';
          break;
        case 'business':
          newState.travelClass = 'business';
          break;
        case 'overweight':
          newState.includeOverweight = true;
          newState.overweightKg = '5';
          break;
        case 'extras':
          newState.hasExtraItems = true;
          newState.extraItems = ['sport_equipment'];
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // --- Сохранение расчета в localStorage ---
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Расчет для ${airlinesData.find(a => a.id === formData.airline)?.name || formData.airline}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedLuggageCalculations', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // --- Удаление сохраненного расчета ---
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedLuggageCalculations', JSON.stringify(updatedSavedCalcs));
  };

  // --- Загрузка сохраненного расчета ---
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Briefcase className="w-8 h-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор массы багажа'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              airlinesData={airlinesData}
              travelClasses={travelClasses}
              destinationTypes={destinationTypes}
              passengerTypes={passengerTypes}
              extraItemsList={extraItemsList}
              loyaltyStatuses={loyaltyStatuses}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <AirlineComparisonTable 
                  results={results}
                  formData={formData}
                  airlinesData={airlinesData}
                  showAllAirlines={showAllAirlines}
                  setShowAllAirlines={setShowAllAirlines}
                />
              </>
            )}

            {/* --- Информационный контент о багаже --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              airlinesData={airlinesData}
              travelClasses={travelClasses}
              destinationTypes={destinationTypes}
            />

            {/* --- Панель информации о выбранной авиакомпании --- */}
            <AirlineInfoSidebar 
              airlineId={formData.airline}
              airlinesData={airlinesData}
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Полезные советы --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .custom-scrollbar {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default LuggageWeightCalculatorPage;